import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { getPublishedNewsById } from '../services/adminService';
import { NewsItem } from '../types/news';
import ImageModal from '../components/common/ImageModal';

const Container = styled.div`
  min-height: 100vh;
  background: rgba(0, 25, 0, 0.5);
  padding: 20px;
  padding-top: 100px;
  
  @media (max-width: 768px) {
    padding-top: 90px;
    padding: 10px;
    padding-top: 90px;
  }
`;

const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
  color: #e0e0e0;
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  padding: 30px;
  backdrop-filter: none;
  
  @media (max-width: 940px) {
    max-width: 95%;
  }
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const NewsHeader = styled.div`
  margin-bottom: 40px;
`;

const BreadcrumbNav = styled.div`
  font-size: 14px;
  color: #888;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  
  a {
    color: #00d4ff;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: #ffffff;
    }
  }
  
  span {
    margin: 0 10px;
    color: #666;
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 5px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: #00d4ff;
  margin-bottom: 20px;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 2px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 15px;
  }
`;

const NewsDetails = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 30px;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const NewsCategory = styled.div`
  display: inline-block;
  padding: 6px 16px;
  background: rgba(0, 212, 255, 0.2);
  color: #00d4ff;
  font-size: 12px;
  border-radius: 20px;
  border: 1px solid #00d4ff;
  font-weight: bold;
`;

const NewsDate = styled.div`
  font-size: 14px;
  color: #888;
  display: flex;
  align-items: center;
  
  &::before {
    content: '📅';
    margin-right: 8px;
  }
`;

const NewsImage = styled.div<{ imageUrl?: string }>`
  width: 100%;
  height: 400px;
  background-image: ${props => props.imageUrl ? `url(${props.imageUrl})` : 'url(/assets/images/news-placeholder.jpg)'};
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: var(--legacy-card-bg);
  margin-bottom: 30px;
  border: 1px solid #00d4ff;
  border-radius: 12px;
  backdrop-filter: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.4);
  }
`;

const ImageGallery = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 30px;
`;

const GalleryImage = styled.div<{ imageUrl: string }>`
  width: calc(33.333% - 7px);
  height: 120px;
  background-image: url(${props => props.imageUrl});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: var(--legacy-card-bg);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: none;
  
  &:hover {
    border-color: #00d4ff;
    transform: scale(1.02);
    box-shadow: 0 0 15px rgba(0, 212, 255, 0.4);
  }
  
  @media (max-width: 768px) {
    width: calc(50% - 5px);
  }
`;

const NewsContent = styled.div`
  font-size: 16px;
  color: #e0e0e0;
  line-height: 1.8;
  margin-bottom: 40px;
  white-space: pre-line;
  background: var(--legacy-card-bg);
  padding: 30px;
  border-radius: 12px;
  border: 1px solid #00d4ff;
  backdrop-filter: none;
  
  @media (max-width: 768px) {
    padding: 20px;
    font-size: 15px;
  }
`;

const BackButton = styled(Link)`
  display: inline-block;
  padding: 12px 24px;
  background: #00d4ff;
  border: 2px solid #00d4ff;
  border-radius: 8px;
  color: #000000;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  text-decoration: none;
  text-transform: uppercase;
  margin-top: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #00d4ff;
    transform: translateY(-2px);
    box-shadow: 0 0 15px rgba(0, 212, 255, 0.4);
  }
`;

const LoadingText = styled.div`
  text-align: center;
  color: #00d4ff;
  font-size: 1.2rem;
  margin: 50px 0;
`;

const ErrorText = styled.div`
  text-align: center;
  color: #ff6b6b;
  font-size: 1.2rem;
  margin: 50px 0;
  padding: 20px;
  background: var(--legacy-card-bg);
  border: 1px solid #ff6b6b;
  border-radius: 8px;
  backdrop-filter: none;
`;


const NewsDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [newsItem, setNewsItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState('');

  useEffect(() => {
    if (id) {
      fetchNewsDetails(id);
    }
  }, [id]);
  

  const fetchNewsDetails = async (newsId: string) => {
    try {
      setLoading(true);
      setError('');
      const data = await getPublishedNewsById(newsId);
      setNewsItem(data);
    } catch (err) {
      console.error('Error loading news details:', err);
      setError('Failed to load news. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Format date as "MM/DD/YYYY"
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Function to get category name in English
  const getCategoryName = (category: string): string => {
    switch(category) {
      case 'General': return 'General';
      case 'Updates': return 'Updates';
      case 'Events': return 'Events';
      case 'Announcements': return 'Announcements';
      case 'Guides': return 'Guides';
      default: return category;
    }
  };
  
  // Get all available images from news item
  const getImages = (): string[] => {
    if (!newsItem) return [];
    
    // If we have imageUrls array with items, use it
    if (newsItem.imageUrls && newsItem.imageUrls.length > 0) {
      return newsItem.imageUrls;
    }
    
    // Otherwise fall back to the single imageUrl if available
    if (newsItem.imageUrl) {
      return [newsItem.imageUrl];
    }
    
    // No images available
    return [];
  };
  
  // Change current displayed image
  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
  };
  
  // Open image in modal
  const openImageModal = (imageUrl: string) => {
    setModalImageUrl(imageUrl);
    setIsModalOpen(true);
  };
  
  // Close modal
  const closeImageModal = () => {
    setIsModalOpen(false);
    setModalImageUrl('');
  };

  // Handle navigation in image modal
  const handleImageNavigation = (newIndex: number) => {
    const images = getImages();
    if (images && images[newIndex]) {
      setCurrentImageIndex(newIndex);
      setModalImageUrl(images[newIndex]);
    }
  };

  if (loading) {
    return (
      <Container>
        <Content>
          <LoadingText>Loading intelligence data...</LoadingText>
        </Content>
      </Container>
    );
  }

  if (error || !newsItem) {
    return (
      <Container>
        <Content>
          <ErrorText>{error || 'News not found'}</ErrorText>
          <BackButton to="/intel">RETURN TO INTEL DATABASE</BackButton>
        </Content>
      </Container>
    );
  }
  
  const images = getImages();
  const mainImage = images.length > 0 ? images[currentImageIndex] : undefined;

  return (
    <Container>
      <Content>
        <NewsHeader>
          <BreadcrumbNav>
            <Link to="/intel">INTEL DATABASE</Link>
            <span>&gt;</span>
            <Link to={`/intel?category=${newsItem.category}`}>
              {getCategoryName(newsItem.category)}
            </Link>
            <span>&gt;</span>
            INTEL FILE #{id}
          </BreadcrumbNav>
          
          <Title>{newsItem.title}</Title>
          
          <NewsDetails>
            <NewsCategory>{getCategoryName(newsItem.category)}</NewsCategory>
            <NewsDate>{formatDate(newsItem.publishedAt || newsItem.createdAt)}</NewsDate>
          </NewsDetails>
        </NewsHeader>
        
        {/* Main displayed image */}
        {mainImage && (
          <NewsImage 
            imageUrl={mainImage} 
            onClick={() => openImageModal(mainImage)}
          />
        )}
        
        {/* Gallery of all images */}
        {images.length > 1 && (
          <ImageGallery>
            {images.map((imageUrl, index) => (
              <GalleryImage 
                key={index} 
                imageUrl={imageUrl}
                onClick={() => {
                  handleImageClick(index);
                  openImageModal(imageUrl);
                }}
                style={{ 
                  border: index === currentImageIndex ? '2px solid #00d4ff' : '1px solid rgba(0, 212, 255, 0.3)',
                  opacity: index === currentImageIndex ? 1 : 0.7
                }}
              />
            ))}
          </ImageGallery>
        )}
        
        <NewsContent>
          {newsItem.content}
        </NewsContent>
        
        <BackButton to="/intel">RETURN TO INTEL DATABASE</BackButton>
      </Content>
      
      {/* Image Modal */}
      <ImageModal
        isOpen={isModalOpen}
        imageUrl={modalImageUrl}
        imageAlt="News image"
        onClose={closeImageModal}
        images={getImages()}
        currentIndex={currentImageIndex}
        onNavigate={handleImageNavigation}
      />
    </Container>
  );
};

export default NewsDetailPage; 